
import { defineComponent } from 'vue';
import { debounce } from 'lodash';
import Alert from '~/components/Alerts/Alert.vue';
import CustomButton from '~/components/Buttons/CustomButton.vue';
import HiddenInput from '~/components/Inputs/HiddenInput.vue';
import SelectInput from '~/components/Inputs/Select.vue';
import TextInput from '~/components/Inputs/Text.vue';
import { reinitiateMicroDeposits, sendBankDeposits } from '@/api/account';
import { validateDigitLength, validateDigitMaxLength } from '@/utils/validators';
import ExampleCheckDialog from '@/components/BankAccountSetup/ExampleCheckDialog.vue';
import { verifyRoutingNumber } from '@/api/payment-options';
import DetailsConfirmationDialog from '@/components/BankAccountSetup/DetailsConfirmationDialog.vue';
import AchProvidersMixin from '@/mixins/AchProvidersMixin';

function generateMask(options: Record<string, unknown>) {
  return {
    showMaskOnHover: false,
    jitMasking: true,
    ...options
  };
}

export default defineComponent({
  name: 'BankDetails',

  components: {
    DetailsConfirmationDialog,
    Alert,
    CustomButton,
    HiddenInput,
    SelectInput,
    TextInput
  },

  mixins: [AchProvidersMixin],

  props: {
    hideInputs: { type: Boolean, default: false }
  },

  data: () => ({
    accountTypes: [
      { text: 'Checking', value: 'checking' },
      { text: 'Savings', value: 'savings' }
    ],
    bankMask: generateMask({
      noValuePatching: false,
      autoUnmask: true,
      mask: '99999999999999999',
    }),
    routingNumberMask: generateMask({
      noValuePatching: true,
      mask: '999999999',
    }),
    bankAccountType: '',
    bankAccountNumber: '',
    bankAccountVerification: '',
    routingNumber: '',
    loading: false,
    validForm: false,
    showError: false,
    routingNumberErrorMessages: [] as string[],
    showConfirmationDialog: false,
    bankAccountBankName: '',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    debouncedValidateRoutingNumber: null as any
  }),

  computed: {
    bankAccountRules() {
      return [(val: string) => validateDigitLength(val, 3, 17) || 'Please enter a valid bank account number.'];
    },

    bankAccountTypeRules() {
      return [(val: string) => !!val || 'Select a Bank Account type'];
    },

    routingNumberRules() {
      return [(val: string) => validateDigitMaxLength(val, 9) || 'Please enter a valid 9-digit routing number.'];
    },

    bankVerificationRules() {
      return [(val: string) => val === this.bankAccountNumber || 'Bank Account doesn\'t match'];
    },

    buttonDisabled() {
      return this.loading || !this.validForm;
    }
  },

  methods: {
  // inputmask emits 2 input events on paste
    async validateRoutingNumber(value: string) {
      this.bankAccountBankName = '';
      this.routingNumberErrorMessages = [];

      if (!validateDigitMaxLength(value, 9)) return;

      try {
        const { data: { bank_account_name } } = await verifyRoutingNumber(value);
        this.bankAccountBankName = bank_account_name;
      } catch (e) {
        this.routingNumberErrorMessages = [
          'The routing number entered is not valid.'
        + ' Please enter the 9 digit routing number used for electronic transfers.'
        ];
      }
    },

    async sendDeposits() {
      if (this.hideInputs) {
        this.loading = true;
        try {
          await reinitiateMicroDeposits();
          this.$emit('success', this.bankAccountNumber.slice(-4));
        } catch {
          this.showError = true;
        } finally {
          this.loading = false;
        }
      } else {
        this.showConfirmationDialog = true;
      }
    },

    async submitBankDetails() {
      this.showConfirmationDialog = false;
      this.loading = true;
      try {
        await sendBankDeposits({
          bank_account_type: this.bankAccountType,
          ach_account_number: this.bankAccountNumber,
          ach_routing_number: this.routingNumber
        }, this.achProvider);
        this.$emit('success', this.bankAccountNumber.slice(-4));
      } catch {
        this.showError = true;
      } finally {
        this.loading = false;
      }
    },

    showExampleCheckDialog() {
      this.$dialog({ component: ExampleCheckDialog });
    }
  },

  created() {
    this.debouncedValidateRoutingNumber = debounce(this.validateRoutingNumber, 300);
  }
});
